import { ElementRef, Fragment, useMemo } from 'react';
import { useCalendarView } from '../providers';
import { EventData } from '../types';
import { groupOverlappingEvents } from '../utils';
import { CalendarEventCardV3 } from './calendar-event-card';
import { OverlappingEvents } from './overlapping-events';

interface EventCardsProps {
  events: EventData[];
  locationName: string;
  calendarDate?: string;
  calendarViewWrapperRef: React.RefObject<ElementRef<'article'>>;
}

export const EventCards: React.FC<EventCardsProps> = ({
  events,
  locationName,
  calendarDate,
  calendarViewWrapperRef,
}) => {
  const { visibleLaneCount } = useCalendarView(['visibleLaneCount']);
  const eventsGroupedByOverlap = useMemo(() => groupOverlappingEvents(events, true), [events]);

  return eventsGroupedByOverlap.map((groupEvents, groupIndex) => {
    /*
      Override the visibleLaneCount to show all out of office and break events.
      This is to ensure that the out of office events are visible on calendar lane instead of carousel.
      It will be rare for a lane to have more than 2 out of office events at a given time. In normal case, it will be max 2, one for provider and one for location.
    */
    const outEventsCount = groupEvents.filter(
      (groupEvent) => groupEvent.eventType === 'outOfOffice' || groupEvent.eventType === 'break'
    ).length;
    const effectiveVisibleLaneCount = Math.max(visibleLaneCount, outEventsCount);

    return (
      <Fragment key={`${groupEvents[0].eventId}-${groupIndex}`}>
        {groupEvents.map((event, index) => (
          <CalendarEventCardV3
            key={`${event.eventId}-${index}`}
            ref={calendarViewWrapperRef}
            id={event.eventId}
            title={event.name}
            startDate={event.startDate}
            calendarDate={calendarDate}
            startHour={event.startHour}
            endHour={event.endHour}
            type={event.eventType}
            laneCount={event.laneCount}
            laneIndex={event.laneIndex}
            providerId={event.providerId}
            providerName={event.providerName}
            locationName={locationName}
            locationId={event.locationId}
            visibleLaneCount={effectiveVisibleLaneCount}
            personStatus={event.personStatus}
            eventStatus={event.status}
          />
        ))}
        <OverlappingEvents
          overlappingEvents={groupEvents}
          containerRef={calendarViewWrapperRef}
          locationName={locationName}
          visibleLaneCount={effectiveVisibleLaneCount}
        />
      </Fragment>
    );
  });
};
